import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useRole } from "../context/RoleContext";
import { getCityDashboard, exportUrl } from "../lib/cityPortal";
import { Download, ArrowRight, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { relativeDate } from "../lib/ownerUi";
import ContextStrip from "../components/ContextStrip";

/**
 * City Staff Dashboard — observational, built around "what changed and
 * what's stuck", not point-in-time counts.
 *
 * The whole dashboard renders inside a single bordered panel so sections
 * never feel like they're hanging in space. Hairlines divide; nothing floats.
 */
export default function CityDashboard() {
  const { user } = useRole();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    setErr(null);
    getCityDashboard(user.id)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(e));
    return () => {
      alive = false;
    };
  }, [user?.id]);

  if (err) {
    return (
      <p style={{ color: "#A23B2E" }} data-testid="city-dashboard-error">
        Could not load the dashboard.
      </p>
    );
  }
  if (!data) {
    return (
      <div
        style={{ color: "#6C6B85", fontStyle: "italic" }}
        data-testid="city-dashboard-loading"
      >
        Loading dashboard…
      </div>
    );
  }

  return (
    <section className="animate-fade-in-up" data-testid="city-dashboard">
      <ContextStrip
        eyebrow="City Staff · Observed Activity"
        title="Dashboard"
        subtitle="What changed, what's stuck, and where it's happening. Permitting decisions live in Accela."
        actions={
          <a
            href={exportUrl("dashboard", user.id, {})}
            className="wn-btn wn-btn-tertiary"
            data-testid="city-dashboard-export"
          >
            <Download size={14} /> Export CSV
          </a>
        }
      />

      {/* Single panel — every section lives inside this container so the page
          reads as one coherent surface. Hairlines divide; nothing floats. */}
      <div
        style={{
          background: "#FBFAFD",
          border: "1px solid #D9D8E4",
          borderRadius: 4,
          overflow: "hidden",
        }}
        data-testid="city-dash-panel"
      >
        <SectionThisWeek deltas={data.weekly_deltas.items} />
        <Hairline />
        <SectionTwoColumn
          attention={data.needs_attention.sections}
          recent={data.recent_activity.items}
        />
        <Hairline />
        <SectionMap
          items={data.map_projects.items}
          center={data.map_projects.center}
          total={data.map_projects.total}
        />
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Layout primitives                                                       */
/* ────────────────────────────────────────────────────────────────────── */

function Hairline() {
  return <div style={{ height: 1, background: "#D9D8E4" }} aria-hidden />;
}

function SectionBlock({ eyebrow, children, testId }) {
  return (
    <div
      data-testid={testId}
      style={{ padding: "20px 24px", background: "#FBFAFD" }}
    >
      {eyebrow && (
        <div
          className="wn-label"
          style={{
            color: "#8F3C23",
            letterSpacing: "0.10em",
            fontSize: 10.5,
            marginBottom: 14,
          }}
        >
          {eyebrow}
        </div>
      )}
      {children}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Tier 1 — THIS WEEK (4 deltas with sparklines)                           */
/* ────────────────────────────────────────────────────────────────────── */

function SectionThisWeek({ deltas }) {
  return (
    <SectionBlock eyebrow="This week" testId="city-this-week">
      <div
        className="grid gap-0"
        style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}
      >
        {deltas.map((m, i) => (
          <DeltaTile
            key={m.key}
            metric={m}
            withDivider={i < deltas.length - 1}
          />
        ))}
      </div>
    </SectionBlock>
  );
}

function DeltaTile({ metric, withDivider }) {
  const { label, this_week, last_week, delta, sparkline } = metric;
  const trendIcon =
    delta > 0 ? <TrendingUp size={12} /> :
      delta < 0 ? <TrendingDown size={12} /> : <Minus size={12} />;
  const trendColor =
    delta > 0 ? "#4F6149" : delta < 0 ? "#A23B2E" : "#6C6B85";

  return (
    <div
      style={{
        padding: "4px 20px",
        borderRight: withDivider ? "1px solid #D9D8E4" : "none",
      }}
      data-testid={`delta-${metric.key}`}
    >
      <div
        className="wn-label"
        style={{ color: "#6C6B85", letterSpacing: "0.06em", fontSize: 10 }}
      >
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span
          className="tabular-nums"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontSize: 28,
            fontWeight: 600,
            color: "#1B1F4B",
            lineHeight: 1.1,
          }}
        >
          {this_week}
        </span>
        <span
          className="tabular-nums inline-flex items-center"
          style={{
            color: trendColor,
            fontSize: 11.5,
            fontWeight: 700,
            gap: 3,
          }}
          data-testid={`delta-${metric.key}-trend`}
        >
          {trendIcon}
          {delta > 0 ? "+" : ""}{delta}
        </span>
      </div>
      <div className="mt-1.5 flex items-end gap-2">
        <Sparkline values={sparkline} />
        <span
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            fontSize: 11,
            color: "#6C6B85",
            whiteSpace: "nowrap",
          }}
        >
          last wk {last_week}
        </span>
      </div>
    </div>
  );
}

function Sparkline({ values }) {
  const w = 84;
  const h = 22;
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? w / (values.length - 1) : w;
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`)
    .join(" ");
  const last = values[values.length - 1] || 0;
  const lastX = (values.length - 1) * step;
  const lastY = h - (last / max) * h;
  return (
    <svg width={w} height={h} aria-hidden style={{ display: "block" }}>
      <polyline
        points={points}
        fill="none"
        stroke="#5B6196"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastX} cy={lastY} r="2.2" fill="#1B1F4B" />
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Tier 2 — Two columns: Needs Attention | Recent Activity                 */
/* ────────────────────────────────────────────────────────────────────── */

function SectionTwoColumn({ attention, recent }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 1px minmax(0, 1fr)",
      }}
      data-testid="city-two-col"
    >
      <NeedsAttention sections={attention} />
      <div style={{ background: "#D9D8E4" }} aria-hidden />
      <RecentActivity items={recent} />
    </div>
  );
}

function NeedsAttention({ sections }) {
  const totalFlagged = sections.reduce((s, sec) => s + sec.total, 0);
  return (
    <SectionBlock
      eyebrow={`Needs attention · ${totalFlagged} flagged`}
      testId="needs-attention"
    >
      <div className="space-y-5">
        {sections.map((sec) => (
          <AttentionSection key={sec.key} section={sec} />
        ))}
      </div>
    </SectionBlock>
  );
}

function AttentionSection({ section }) {
  const empty = section.items.length === 0;
  return (
    <div data-testid={`attention-${section.key}`}>
      <div className="flex items-baseline justify-between gap-3">
        <h3
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            fontSize: 14,
            color: "#1B1F4B",
            lineHeight: 1.3,
          }}
        >
          {section.label}
        </h3>
        <span
          className="tabular-nums"
          style={{
            color: empty ? "#9AA0C0" : "#A23B2E",
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: "0.05em",
          }}
        >
          {section.total}
        </span>
      </div>
      {empty ? (
        <p
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 12.5,
            margin: "6px 0 0",
          }}
          data-testid={`attention-${section.key}-empty`}
        >
          All clear — nothing past threshold.
        </p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {section.items.map((item) => (
            <AttentionItem
              key={item.id || item.case_id}
              section={section.key}
              item={item}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function AttentionItem({ section, item }) {
  const days =
    item.days_waiting ||
    item.days_in_stage ||
    item.days_since_activity ||
    null;
  return (
    <li
      className="flex items-baseline justify-between gap-3"
      style={{ fontSize: 12.5, color: "#272636" }}
    >
      <Link
        to={item.href}
        style={{ color: "#1B1F4B", fontWeight: 600 }}
        className="hover:underline"
        data-testid={`attention-${section}-item-${item.id || item.case_id}`}
      >
        {item.name || item.case_id || item.id}
      </Link>
      <span
        className="tabular-nums"
        style={{ color: "#A23B2E", fontSize: 11.5, whiteSpace: "nowrap" }}
      >
        {days != null ? `${days}d` : item.last_sync_at
          ? relativeDate(item.last_sync_at)
          : "—"}
      </span>
    </li>
  );
}

function RecentActivity({ items }) {
  return (
    <SectionBlock
      eyebrow={`Activity · last ${items.length}`}
      testId="recent-activity"
    >
      {items.length === 0 ? (
        <p
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 13,
          }}
          data-testid="recent-activity-empty"
        >
          No civic-scope events recorded yet.
        </p>
      ) : (
        <ul className="space-y-2.5" data-testid="recent-activity-list">
          {items.map((a, i) => (
            <li
              key={a.id || i}
              className="flex items-start gap-2.5"
              data-testid={`activity-row-${i}`}
            >
              <span
                aria-hidden
                style={{
                  marginTop: 5,
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: "#B14E2F",
                  flexShrink: 0,
                }}
              />
              <div className="min-w-0 flex-1">
                <div
                  className="flex items-baseline justify-between gap-2 flex-wrap"
                  style={{ fontSize: 12.5, color: "#272636", lineHeight: 1.4 }}
                >
                  <span>
                    <strong style={{ color: "#1B1F4B" }}>
                      {prettyAction(a.action)}
                    </strong>
                    {" · "}
                    {a.actor_name || a.actor_id || "System"}
                  </span>
                  <span
                    className="tabular-nums"
                    style={{
                      fontFamily: '"Newsreader", Georgia, serif',
                      fontStyle: "italic",
                      color: "#6C6B85",
                      fontSize: 11.5,
                    }}
                  >
                    {relativeDate(a.at)}
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      <Link
        to="/city/audit"
        className="wn-btn wn-btn-tertiary inline-flex items-center mt-3"
        style={{ paddingLeft: 0, gap: 6, fontSize: 12 }}
        data-testid="recent-activity-open-audit"
      >
        Open full audit trail <ArrowRight size={12} />
      </Link>
    </SectionBlock>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Tier 3 — Geographic anchor                                              */
/* ────────────────────────────────────────────────────────────────────── */

const STAGE_COLORS = {
  Selection:                "#9AA0C0",
  Consultation:             "#5B6196",
  Purchase:                 "#1B1F4B",
  "Expedited Review":       "#B98A3C",
  Build:                    "#B14E2F",
  "Construction Inspection":"#8F3C23",
  Complete:                 "#4F6149",
};

function SectionMap({ items, center, total }) {
  return (
    <SectionBlock
      eyebrow={`Geography · ${total} active project${total === 1 ? "" : "s"}`}
      testId="city-map"
    >
      <div
        style={{
          height: 280,
          borderRadius: 4,
          overflow: "hidden",
          border: "1px solid #D9D8E4",
          background: "#fff",
        }}
      >
        <MapContainer
          center={center}
          zoom={12}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={false}
          attributionControl={false}
        >
          <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
          {items.map((p) => (
            <CircleMarker
              key={p.id}
              center={[p.lat, p.lng]}
              radius={7}
              pathOptions={{
                fillColor: STAGE_COLORS[p.current_stage] || "#5B6196",
                color: "#fff",
                weight: 1.6,
                opacity: 1,
                fillOpacity: 0.92,
              }}
            >
              <Tooltip direction="top" offset={[0, -6]} opacity={1}>
                <div
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 600,
                    color: "#1B1F4B",
                    fontSize: 12,
                  }}
                >
                  {p.name}
                </div>
                <div style={{ color: "#6C6B85", fontSize: 11 }}>
                  {p.address} · {p.neighborhood}
                </div>
                <div
                  style={{ color: "#272636", fontSize: 11, marginTop: 2 }}
                >
                  Stage: <strong>{p.current_stage}</strong>
                </div>
              </Tooltip>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
          {Object.entries(STAGE_COLORS).map(([stage, color]) => (
            <span
              key={stage}
              className="inline-flex items-center"
              style={{ gap: 5, fontSize: 11, color: "#272636" }}
            >
              <span
                aria-hidden
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: color,
                  display: "inline-block",
                }}
              />
              {stage}
            </span>
          ))}
        </div>
        <Link
          to="/city/heatmap"
          className="wn-btn wn-btn-tertiary inline-flex items-center"
          style={{ paddingLeft: 0, gap: 6, fontSize: 12 }}
          data-testid="city-map-open-heatmap"
        >
          Open Heatmap <ArrowRight size={12} />
        </Link>
      </div>
    </SectionBlock>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Helpers                                                                 */
/* ────────────────────────────────────────────────────────────────────── */

function prettyAction(a) {
  if (!a) return "—";
  return a
    .replace(/_/g, " ")
    .replace(/\./g, " → ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
